import React, { useMemo, useRef, useEffect, useState, useLayoutEffect, useCallback } from 'react';
import { format, startOfDay, isSameDay, parseISO, getDay } from 'date-fns';
import { CalendarBooking } from '../lib/bookings';
import { Resource } from '../lib/resources';
import { Plus, MapPin, Mail, Phone, User, ChevronRight, X, Package } from 'lucide-react';
import { ResourceWorkingAvailability } from '../lib/resourceAvailability';
import { useDismissOverlayProtectingCalendarCreate } from '../hooks/useDismissOverlayProtectingCalendarCreate';
import { hapticSelection } from '../lib/haptics';

interface DayHours {
  enabled: boolean;
  open: string;
  close: string;
  closed: boolean;
}

interface BusinessHours {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
  [key: string]: DayHours | undefined;
}

interface GoogleCalendarEvent {
  id: string;
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  location: string | null;
  description: string | null;
  resource_id: string | null;
}

interface WeekViewProps {
  weekDays: Date[];
  resources: Resource[];
  bookings: CalendarBooking[];
  timeOffRequests: CalendarBooking[];
  fieldQuoteEvents?: CalendarBooking[];
  onBookingClick: (event: any) => void;
  onTimeSlotClick?: (
    date: Date,
    resourceId: string,
    anchorRect?: { top: number; left: number; width: number; height: number },
    /** Exclusive end of the selected range when the user drag-selected multiple slots. */
    endDate?: Date
  ) => void;
  isLoading: boolean;
  businessHours?: BusinessHours | null;
  resourceAvailabilities?: Map<string, ResourceWorkingAvailability>;
  isDraggingMode?: boolean;
  draggedBookingId?: string;
  onDropBooking?: (date: Date, resourceId?: string) => void;
  onResourceClick?: (resource: Resource) => void;
  weekViewFilter?: 'all' | 'resources' | 'assets';
  googleCalendarEvents?: GoogleCalendarEvent[];
  onGcalEventClick?: (event: GoogleCalendarEvent) => void;
  /** Keep this empty slot tinted while the create popover is open. */
  highlightedSlotKey?: string | null;
  /** Keep a drag-selected (or create) time range tinted while the popover is open. */
  highlightedRange?: {
    resourceId: string;
    startMs: number;
    endMs: number;
  } | null;
}

/** Stable key for an empty calendar slot (resource + local date/time to the minute). */
export function weekSlotKey(date: Date, resourceId: string): string {
  return `${resourceId}|${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
}

const SLOT_MS = 30 * 60 * 1000;
/** Hold still this long before range-drag arms (scroll/pan before this cancels). */
const HOLD_TO_DRAG_MS = 200;
/** Finger jitter allowed while holding; larger movement before arm = scroll. */
const MOVE_CANCEL_BEFORE_HOLD_PX = 28;
const DRAG_SELECT_THRESHOLD_PX = 6;

function findSlotElAtPoint(resourceId: string, clientX: number, clientY: number): HTMLElement | null {
  const slots = document.querySelectorAll('[data-calendar-create-slot]');
  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < slots.length; i++) {
    const el = slots[i] as HTMLElement;
    if (el.getAttribute('data-slot-resource') !== resourceId) continue;
    const r = el.getBoundingClientRect();
    // Prefer slots that contain the point; otherwise nearest by vertical center
    if (clientY >= r.top && clientY <= r.bottom && clientX >= r.left - 8 && clientX <= r.right + 8) {
      return el;
    }
    const cy = (r.top + r.bottom) / 2;
    const dist = Math.abs(clientY - cy);
    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }
  // Only accept nearest if reasonably close (within ~2 slot heights)
  if (best && bestDist <= 70) return best;
  return null;
}

function slotOverlapsRange(slotStart: Date, rangeStartMs: number, rangeEndMs: number): boolean {
  const start = slotStart.getTime();
  const end = start + SLOT_MS;
  return start < rangeEndMs && end > rangeStartMs;
}

// Generate time slots based on business hours for the week
const generateTimeSlots = (weekDays: Date[], businessHours?: BusinessHours | null): { slots: string[]; startHour: number; endHour: number } => {
  // Default to 6 AM - 11 PM if no business hours
  if (!businessHours) {
    const slots: string[] = [];
    for (let hour = 6; hour < 23; hour++) {
      let displayHour = hour;
      let period = 'am';
      
      if (hour === 12) {
        displayHour = 12;
        period = 'pm';
      } else if (hour > 12) {
        displayHour = hour - 12;
        period = 'pm';
      }
      
      slots.push(`${displayHour}${period}`);
    }
    // Add closing hour as label only
    slots.push('11pm');
    return { slots, startHour: 6, endHour: 23 };
  }

  // Find min open time and max close time across all days in the week (as decimals)
  let minHourDecimal = 24; // Start with latest possible time
  let maxHourDecimal = 0;  // Start with earliest possible time

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  weekDays.forEach(day => {
    const dayName = dayNames[day.getDay()];
    const dayHours = businessHours[dayName];
    
    if (dayHours && dayHours.enabled && !dayHours.closed) {
      // Parse open time (format: "HH:MM")
      const [openHour, openMin] = dayHours.open.split(':').map(Number);
      const openHourDecimal = openHour + openMin / 60;
      
      // Parse close time (format: "HH:MM")
      const [closeHour, closeMin] = dayHours.close.split(':').map(Number);
      const closeHourDecimal = closeHour + closeMin / 60;
      
      // Use actual decimal times (don't round)
      minHourDecimal = Math.min(minHourDecimal, openHourDecimal);
      maxHourDecimal = Math.max(maxHourDecimal, closeHourDecimal);
    }
  });

  // If no valid hours found, default to 6 AM - 11 PM
  if (minHourDecimal >= 24 || maxHourDecimal <= 0) {
    minHourDecimal = 6;
    maxHourDecimal = 23;
  }

  // Round down start to nearest hour for slot generation, but keep decimal for positioning
  const minHour = Math.floor(minHourDecimal);
  const maxHour = Math.ceil(maxHourDecimal);

  console.log('🔧 generateTimeSlots - calculated hours:', {
    minHourDecimal,
    maxHourDecimal,
    minHour,
    maxHour,
    loopWillRun: minHour <= maxHour,
    iterations: maxHour - minHour + 1
  });

  // Generate slots from minHour to maxHour with readable format (9am, 10am, 12pm, etc.)
  // Include all hours from start to end (inclusive of closing hour)
  const slots: string[] = [];
  for (let hour = minHour; hour <= maxHour; hour++) {
    let displayHour = hour;
    let period = 'am';
    
    if (hour === 0) {
      displayHour = 12;
      period = 'am';
    } else if (hour === 12) {
      displayHour = 12;
      period = 'pm';
    } else if (hour > 12) {
      displayHour = hour - 12;
      period = 'pm';
    }
    
    const slotLabel = `${displayHour}${period}`;
    slots.push(slotLabel);
    
    if (hour === minHour || hour === maxHour) {
      console.log(`🔧 Added slot for hour ${hour}: "${slotLabel}"`);
    }
  }

  console.log('🔧 generateTimeSlots - final slots array:', slots);

  // Return the actual decimal start time for accurate positioning
  return { slots, startHour: minHourDecimal, endHour: maxHourDecimal };
};

// Get resource working hours for a specific day (returns start and end times as Date objects)
const getResourceWorkingHoursForDay = (
  day: Date,
  resourceAvailability: ResourceWorkingAvailability | null
): { startTime: Date; endTime: Date } | null => {
  if (!resourceAvailability) {
    return null; // No availability set
  }

  const dayOfWeek = getDay(day); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayOfWeek] as keyof ResourceWorkingAvailability;
  const dayAvailability = resourceAvailability[dayName];

  if (!dayAvailability || !dayAvailability.enabled || !dayAvailability.shifts || dayAvailability.shifts.length === 0) {
    return null; // Day not enabled or no shifts
  }

  const dayStart = startOfDay(day);
  
  // Find the earliest start and latest end across all shifts for this day
  let earliestStartDecimal = 24; // Start with max hour
  let latestEndDecimal = 0; // Start with min hour

  for (const shift of dayAvailability.shifts) {
    const [startHour, startMin] = shift.start.split(':').map(Number);
    const [endHour, endMin] = shift.end.split(':').map(Number);
    const startDecimal = startHour + startMin / 60;
    const endDecimal = endHour + endMin / 60;
    
    earliestStartDecimal = Math.min(earliestStartDecimal, startDecimal);
    latestEndDecimal = Math.max(latestEndDecimal, endDecimal);
  }

  // Create Date objects for start and end times
  const startTime = new Date(dayStart);
  startTime.setHours(Math.floor(earliestStartDecimal), Math.round((earliestStartDecimal - Math.floor(earliestStartDecimal)) * 60), 0, 0);
  
  const endTime = new Date(dayStart);
  endTime.setHours(Math.floor(latestEndDecimal), Math.round((latestEndDecimal - Math.floor(latestEndDecimal)) * 60), 0, 0);

  return { startTime, endTime };
};

// Calculate position and height for a booking block
const calculateBookingPosition = (
  booking: CalendarBooking,
  day: Date,
  startHour: number,
  endHour: number,
  resourceAvailability: ResourceWorkingAvailability | null = null
): { top: number; height: number; visible: boolean } => {
  const bookingStart = parseISO(booking.start);
  const bookingEnd = parseISO(booking.end);
  const dayStart = startOfDay(day);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59);

  // Check if booking overlaps with this day
  if (bookingEnd <= dayStart || bookingStart >= dayEnd) {
    return { top: 0, height: 0, visible: false };
  }

  // Calculate the actual start and end times for this day
  let actualStart = bookingStart < dayStart ? dayStart : bookingStart;
  let actualEnd = bookingEnd > dayEnd ? dayEnd : bookingEnd;

  // For buffer blocks, don't clamp to business hours - they can extend beyond business hours
  const isBufferBlock = booking.extendedProps?.is_buffer_block === true;
  
  if (!isBufferBlock) {
    // Try to clamp to resource working hours if available
    const resourceHours = getResourceWorkingHoursForDay(day, resourceAvailability);
    
    if (resourceHours) {
      // Clamp to resource working hours for this day
      if (actualStart < resourceHours.startTime) {
        actualStart = new Date(resourceHours.startTime);
      }
      if (actualEnd > resourceHours.endTime) {
        actualEnd = new Date(resourceHours.endTime);
      }
      
      // If after clamping, the booking is completely outside resource hours, don't show it
      if (actualStart >= resourceHours.endTime || actualEnd <= resourceHours.startTime) {
        return { top: 0, height: 0, visible: false };
      }
    } else if (resourceAvailability !== null) {
      // Resource availability exists but this day is not enabled - don't show booking
      return { top: 0, height: 0, visible: false };
    } else {
      // No resource availability set - clamp to business hours as fallback
      const dayStartBusinessHours = new Date(dayStart);
      const startHourInt = Math.floor(startHour);
      const startMinInt = Math.round((startHour - startHourInt) * 60);
      dayStartBusinessHours.setHours(startHourInt, startMinInt, 0, 0);
      if (actualStart < dayStartBusinessHours) {
        actualStart = dayStartBusinessHours;
      }

      const dayEndBusinessHours = new Date(dayStart);
      const endHourInt = Math.floor(endHour);
      const endMinInt = Math.round((endHour - endHourInt) * 60);
      dayEndBusinessHours.setHours(endHourInt, endMinInt, 59, 999);
      if (actualEnd > dayEndBusinessHours) {
        actualEnd = dayEndBusinessHours;
      }
    }
  }

  // Calculate hour decimals for positioning
  const startHourDecimal = actualStart.getHours() + actualStart.getMinutes() / 60;
  const endHourDecimal = actualEnd.getHours() + actualEnd.getMinutes() / 60;
  
  // For buffer blocks, allow them to extend beyond visible hours but still position them correctly
  let clampedStartHour: number;
  let clampedEndHour: number;
  
  if (isBufferBlock) {
    // Buffer blocks can extend beyond business hours
    // Check if buffer block overlaps with visible range at all
    // Overlap exists if: startHourDecimal < endHour && endHourDecimal > startHour
    const overlapsVisibleRange = startHourDecimal < endHour && endHourDecimal > startHour;
    
    if (!overlapsVisibleRange) {
      // Buffer block is completely outside visible range
      return { top: 0, height: 0, visible: false };
    }
    
    // For buffer blocks that overlap, clamp to visible range for display
    // But show from the top if it starts before visible range
    clampedStartHour = Math.max(startHour, startHourDecimal);
    clampedEndHour = Math.min(endHour, endHourDecimal);
  } else {
    // Regular bookings: clamp to visible hours (business hours) for positioning
    clampedStartHour = Math.max(startHour, Math.min(endHour, startHourDecimal));
    clampedEndHour = Math.max(startHour, Math.min(endHour, endHourDecimal));
    
    // If booking is completely outside visible range, don't show it
    if (endHourDecimal < startHour || startHourDecimal > endHour) {
      return { top: 0, height: 0, visible: false };
    }
  }
  
  // The grid rows start at Math.floor(startHour), so position calculations
  // must reference the floored hour to align with the actual grid.
  const gridStartHour = Math.floor(startHour);
  
  const top = isBufferBlock && startHourDecimal < startHour 
    ? 0 
    : Math.max(0, (clampedStartHour - gridStartHour) * 60);
  
  // For height, use grid-aligned reference when buffer starts before visible range
  const visibleStart = isBufferBlock && startHourDecimal < startHour ? gridStartHour : clampedStartHour;
  const visibleEnd = isBufferBlock && endHourDecimal > endHour ? endHour : clampedEndHour;
  const height = Math.max(20, (visibleEnd - visibleStart) * 60); // 60px per hour, minimum 20px

  // If the booking is completely outside visible hours (and not a buffer block), don't show it
  if (!isBufferBlock && clampedEndHour <= clampedStartHour) {
    return { top: 0, height: 0, visible: false };
  }

  return { top, height, visible: true };
};

// Check if two bookings overlap on a given day
const bookingsOverlapOnDay = (
  booking1: CalendarBooking,
  booking2: CalendarBooking,
  day: Date
): boolean => {
  const dayStart = startOfDay(day);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);

  const b1Start = parseISO(booking1.start);
  const b1End = parseISO(booking1.end);
  const b2Start = parseISO(booking2.start);
  const b2End = parseISO(booking2.end);

  // Get the actual time range for each booking on this day
  const b1DayStart = b1Start < dayStart ? dayStart : b1Start;
  const b1DayEnd = b1End > dayEnd ? dayEnd : b1End;
  const b2DayStart = b2Start < dayStart ? dayStart : b2Start;
  const b2DayEnd = b2End > dayEnd ? dayEnd : b2End;

  // Check if they overlap: b1DayStart < b2DayEnd && b1DayEnd > b2DayStart
  return b1DayStart < b2DayEnd && b1DayEnd > b2DayStart;
};

// Group bookings into overlapping sets and assign column indices
const assignBookingColumns = (
  bookings: CalendarBooking[],
  day: Date
): Map<string, { column: number; totalColumns: number }> => {
  const columnAssignments = new Map<string, { column: number; totalColumns: number }>();
  
  if (bookings.length === 0) return columnAssignments;

  // Sort bookings by start time
  const sortedBookings = [...bookings].sort((a, b) => {
    const aStart = parseISO(a.start);
    const bStart = parseISO(b.start);
    return aStart.getTime() - bStart.getTime();
  });

  // Track which column each booking is assigned to
  const bookingColumns = new Map<string, number>();
  let maxColumns = 0;

  // For each booking, assign it to the earliest available column
  for (const booking of sortedBookings) {
    // Find all bookings that overlap with this one and are already assigned
    const overlappingBookings = sortedBookings.filter(b => 
      b.id !== booking.id && 
      bookingsOverlapOnDay(booking, b, day) &&
      bookingColumns.has(b.id)
    );

    // Find the earliest column (0, 1, 2, ...) that doesn't conflict
    const usedColumns = new Set(overlappingBookings.map(b => bookingColumns.get(b.id)!));
    let assignedColumn = 0;
    
    // Find the first available column
    while (usedColumns.has(assignedColumn)) {
      assignedColumn++;
    }

    bookingColumns.set(booking.id, assignedColumn);
    maxColumns = Math.max(maxColumns, assignedColumn + 1);
  }

  // Set the assignments with total columns
  for (const [bookingId, column] of bookingColumns.entries()) {
    columnAssignments.set(bookingId, { column, totalColumns: maxColumns });
  }

  return columnAssignments;
};

// Get bookings for a specific resource and day (including multi-day bookings)
const getBookingsForResourceAndDay = (
  bookings: CalendarBooking[],
  resourceId: string | null,
  day: Date
): CalendarBooking[] => {
  const dayStart = startOfDay(day);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);

  const matchingBookings = bookings.filter(booking => {
    const bookingStart = parseISO(booking.start);
    const bookingEnd = parseISO(booking.end);

    // Check if booking overlaps with this day (not just starts on it)
    // Booking overlaps if: bookingStart < dayEnd AND bookingEnd > dayStart
    const overlaps = bookingStart < dayEnd && bookingEnd > dayStart;
    if (!overlaps) return false;

    // If resourceId is null or 'unassigned', show unassigned bookings (no resources AND no assets)
    if (resourceId === null || resourceId === 'unassigned') {
      const hasResources = booking.extendedProps?.resources && booking.extendedProps.resources.length > 0;
      const hasAssets = booking.extendedProps?.booking_assets && booking.extendedProps.booking_assets.length > 0;
      return !hasResources && !hasAssets;
    }

    // Check if this booking has this resource or asset assigned
    const bookingResources = booking.extendedProps?.resources || [];
    const bookingResourceIds = bookingResources.map((r: any) => r?.id).filter(Boolean);
    
    const bookingAssets = booking.extendedProps?.booking_assets || [];
    const bookingAssetIds = bookingAssets.map((a: any) => a?.id || a?.asset_id).filter(Boolean);
    
    const isBufferBlock = booking.extendedProps?.is_buffer_block === true;
    
    const hasResource = 
      bookingResourceIds.includes(resourceId) ||
      bookingAssetIds.includes(resourceId) ||
      booking.resourceId === resourceId || 
      booking.extendedProps?.resource_id === resourceId ||
      (isBufferBlock && booking.extendedProps?.resource_id === resourceId);

    // Debug logging for buffer blocks that don't match
    if (isBufferBlock && !hasResource) {
      console.log('🔍 Buffer block resource mismatch:', {
        bufferBlockId: booking.id,
        checkingResourceId: resourceId,
        bookingResourceId: booking.resourceId,
        extendedPropsResourceId: booking.extendedProps?.resource_id,
        bookingResourceIds,
        hasResource
      });
    }

    return hasResource;
  });

  // Debug: Log why bookings aren't matching (only for first few checks to avoid spam)
  if (matchingBookings.length === 0 && bookings.length > 0 && resourceId) {
    const sampleBooking = bookings[0];
    const bookingResources = sampleBooking.extendedProps?.resources || [];
    const bookingResourceIds = bookingResources.map((r: any) => r?.id).filter(Boolean);
    
    console.log('Resource matching debug:', {
      checkingResourceId: resourceId,
      sampleBookingId: sampleBooking.id,
      sampleBookingTitle: sampleBooking.title,
      bookingResourceIds: bookingResourceIds,
      bookingResourceId: sampleBooking.resourceId,
      extendedPropsResourceId: sampleBooking.extendedProps?.resource_id,
      bookingResources: bookingResources.map((r: any) => ({ id: r.id, name: `${r.first_name} ${r.last_name}` })),
      matchFound: bookingResourceIds.includes(resourceId) || sampleBooking.resourceId === resourceId || sampleBooking.extendedProps?.resource_id === resourceId
    });
  }

  return matchingBookings;
};

// Check if a specific time range is covered by any shift
const isTimeRangeCovered = (
  startTime: number, // decimal hour (e.g., 12.0 for 12:00 PM)
  endTime: number, // decimal hour (e.g., 12.5 for 12:30 PM)
  day: Date,
  resourceAvailability: ResourceWorkingAvailability | null
): boolean => {
  if (!resourceAvailability) {
    return false;
  }

  const dayOfWeek = getDay(day);
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayOfWeek] as keyof ResourceWorkingAvailability;
  const dayAvailability = resourceAvailability[dayName];

  if (!dayAvailability || !dayAvailability.enabled || !dayAvailability.shifts || dayAvailability.shifts.length === 0) {
    return false;
  }

  // Check if the time range is fully covered by any shift
  for (const shift of dayAvailability.shifts) {
    const [startHour, startMin] = shift.start.split(':').map(Number);
    const [endHour, endMin] = shift.end.split(':').map(Number);
    const shiftStart = startHour + startMin / 60;
    const shiftEnd = endHour + endMin / 60;
    
    // Time range is covered if it's completely within a shift
    if (startTime >= shiftStart && endTime <= shiftEnd) {
      return true;
    }
  }

  return false;
};

// Check if a time slot (hour block) is available for a resource on a given day
const isTimeSlotAvailable = (
  hour: number,
  day: Date,
  resourceAvailability: ResourceWorkingAvailability | null,
  businessStartHour?: number
): boolean => {
  // If no availability set, resource is not available (grey out all)
  if (!resourceAvailability) {
    return false;
  }

  const dayOfWeek = getDay(day); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayOfWeek] as keyof ResourceWorkingAvailability;
  const dayAvailability = resourceAvailability[dayName];

  // If day is not enabled or has no shifts, resource is not available
  if (!dayAvailability || !dayAvailability.enabled || !dayAvailability.shifts || dayAvailability.shifts.length === 0) {
    return false;
  }

  // Check if the hour block (hour to hour+1) overlaps with any shift
  // But if this is the first hour and business hours start later, check partial hour
  let hourStart = hour;
  const hourEnd = hour + 1;
  
  // If this is the first hour slot and business hours start later in the hour, adjust the start
  if (businessStartHour !== undefined && hour === Math.floor(businessStartHour) && businessStartHour > hour) {
    // This hour slot starts partway through (e.g., 6:30am in the 6am slot)
    // Only check availability from the actual start time
    hourStart = businessStartHour;
  }
  
  // Check if ANY part of the hour block overlaps with ANY shift
  for (const shift of dayAvailability.shifts) {
    const [startHour, startMin] = shift.start.split(':').map(Number);
    const [endHour, endMin] = shift.end.split(':').map(Number);
    const shiftStart = startHour + startMin / 60;
    const shiftEnd = endHour + endMin / 60;
    
    // Check if hour block overlaps with shift (hour block overlaps if: hourStart < shiftEnd && hourEnd > shiftStart)
    // This means the hour block has at least some time that is within the shift
    if (hourStart < shiftEnd && hourEnd > shiftStart) {
      return true;
    }
  }

  // No shifts overlap with this hour block
  return false;
};

export default function WeekView({
  weekDays,
  resources,
  bookings,
  timeOffRequests,
  fieldQuoteEvents = [],
  onBookingClick,
  onTimeSlotClick,
  isLoading,
  businessHours,
  resourceAvailabilities = new Map(),
  isDraggingMode = false,
  draggedBookingId,
  onDropBooking,
  onResourceClick,
  weekViewFilter = 'all',
  googleCalendarEvents = [],
  onGcalEventClick,
  highlightedSlotKey = null,
  highlightedRange = null,
}: WeekViewProps) {
  console.log('🎯 WeekView FUNCTION CALLED with props:', {
    weekDaysLength: weekDays?.length || 0,
    resourcesLength: resources?.length || 0,
    bookingsLength: bookings?.length || 0,
    timeOffLength: timeOffRequests?.length || 0,
    fieldQuotesLength: fieldQuoteEvents?.length || 0,
    isLoading,
    hasBusinessHours: !!businessHours,
    resourceAvailabilitiesSize: resourceAvailabilities?.size || 0
  });
  
  const { slots: timeSlots, startHour, endHour } = useMemo(() => {
    console.log('🔧 generateTimeSlots called with:', {
      weekDaysLength: weekDays?.length || 0,
      hasBusinessHours: !!businessHours
    });
    const result = generateTimeSlots(weekDays, businessHours);
    console.log('🔧 generateTimeSlots result:', result);
    return result;
  }, [weekDays, businessHours]);
  const today = new Date();
  const timeColumnRef = useRef<HTMLDivElement>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track the week we've scrolled for
  const scrolledWeekRef = useRef<string | null>(null);
  // State to track if initial scroll is complete (to show content)
  const [isScrollReady, setIsScrollReady] = useState(false);

  // Press → (optional hold) → drag empty-slot range selection
  // Quick tap = create. Move before hold = scroll. Hold then drag = range select.
  type SlotDragSelect = {
    pointerId: number;
    resourceId: string;
    originStartMs: number;
    originEndMs: number;
    rangeStartMs: number;
    rangeEndMs: number;
    originClientX: number;
    originClientY: number;
    /** pending = waiting for hold; armed = drag-select active; cancelled = scroll/pan */
    phase: 'pending' | 'armed' | 'cancelled';
    hasDragged: boolean;
    captureEl: HTMLElement | null;
    anchorRect: { top: number; left: number; width: number; height: number };
  };
  const [slotDragSelect, setSlotDragSelect] = useState<SlotDragSelect | null>(null);
  // Ref is source of truth during the gesture — do not sync from state (stale renders wipe updates).
  const slotDragSelectRef = useRef<SlotDragSelect | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const docCleanupRef = useRef<(() => void) | null>(null);
  const armedScrollLockRef = useRef<(() => void) | null>(null);
  const [committedSlotRange, setCommittedSlotRange] = useState<{
    resourceId: string;
    startMs: number;
    endMs: number;
  } | null>(null);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current != null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const clearDocListeners = useCallback(() => {
    docCleanupRef.current?.();
    docCleanupRef.current = null;
  }, []);

  const clearArmedScrollLock = useCallback(() => {
    armedScrollLockRef.current?.();
    armedScrollLockRef.current = null;
  }, []);

  const cancelSlotDragSelect = useCallback((pointerId?: number) => {
    const drag = slotDragSelectRef.current;
    if (!drag) return;
    if (pointerId !== undefined && drag.pointerId !== pointerId) return;
    clearHoldTimer();
    clearArmedScrollLock();
    clearDocListeners();
    slotDragSelectRef.current = null;
    setSlotDragSelect(null);
  }, [clearHoldTimer, clearDocListeners, clearArmedScrollLock]);

  const armSlotDragSelect = useCallback(() => {
    const drag = slotDragSelectRef.current;
    if (!drag || drag.phase !== 'pending') return;

    const armed: SlotDragSelect = { ...drag, phase: 'armed' };
    slotDragSelectRef.current = armed;
    setSlotDragSelect(armed);
    void hapticSelection();

    // Lock scroll immediately (don't wait for useEffect) so the first post-hold move tracks the range.
    clearArmedScrollLock();
    const preventScroll = (ev: TouchEvent) => {
      ev.preventDefault();
    };
    document.addEventListener('touchmove', preventScroll, { passive: false });
    armedScrollLockRef.current = () => {
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [clearArmedScrollLock]);

  const updateSlotDragSelectFromPoint = useCallback((clientX: number, clientY: number) => {
    const drag = slotDragSelectRef.current;
    if (!drag || drag.phase === 'cancelled') return;

    const dx = clientX - drag.originClientX;
    const dy = clientY - drag.originClientY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const moved = Math.max(absDx, absDy);

    // Before hold completes, clear movement = scroll/pan — abandon create
    if (drag.phase === 'pending') {
      if (moved >= MOVE_CANCEL_BEFORE_HOLD_PX) {
        clearHoldTimer();
        clearDocListeners();
        slotDragSelectRef.current = null;
        setSlotDragSelect(null);
      }
      return;
    }

    // Armed: expand/contract range from finger position in this resource column
    const hasDragged =
      drag.hasDragged || absDy >= DRAG_SELECT_THRESHOLD_PX || absDx >= DRAG_SELECT_THRESHOLD_PX;

    let rangeStartMs = drag.originStartMs;
    let rangeEndMs = drag.originEndMs;

    if (hasDragged) {
      const slotEl = findSlotElAtPoint(drag.resourceId, clientX, clientY);
      if (slotEl) {
        const startMsAttr = slotEl.getAttribute('data-slot-start-ms');
        if (startMsAttr) {
          const hoverStartMs = Number(startMsAttr);
          if (!Number.isNaN(hoverStartMs)) {
            const hoverEndMs = hoverStartMs + SLOT_MS;
            rangeStartMs = Math.min(drag.originStartMs, hoverStartMs);
            rangeEndMs = Math.max(drag.originEndMs, hoverEndMs);
          }
        }
      }
    }

    const next: SlotDragSelect = {
      ...drag,
      hasDragged,
      rangeStartMs,
      rangeEndMs,
    };
    slotDragSelectRef.current = next;
    setSlotDragSelect(next);
  }, [clearHoldTimer, clearDocListeners]);

  const finishSlotDragSelect = useCallback((
    e: { pointerId: number; clientX: number; clientY: number; currentTarget?: EventTarget | null }
  ) => {
    const drag = slotDragSelectRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    clearHoldTimer();

    const phase = drag.phase;
    if (phase === 'cancelled') {
      clearArmedScrollLock();
      clearDocListeners();
      slotDragSelectRef.current = null;
      setSlotDragSelect(null);
      return;
    }

    // Still pending hold = quick tap → single-slot create
    if (phase === 'pending') {
      clearArmedScrollLock();
      clearDocListeners();
      const tapDrag = drag;
      slotDragSelectRef.current = null;
      setSlotDragSelect(null);
      setCommittedSlotRange(null);
      if (!onTimeSlotClick) return;
      onTimeSlotClick(
        new Date(tapDrag.originStartMs),
        tapDrag.resourceId,
        tapDrag.anchorRect,
        undefined
      );
      return;
    }

    // Armed: finalize range (may still be single slot if no drag)
    updateSlotDragSelectFromPoint(e.clientX, e.clientY);
    const finalDrag = slotDragSelectRef.current ?? drag;

    clearArmedScrollLock();
    clearDocListeners();

    const start = new Date(finalDrag.rangeStartMs);
    const end = new Date(finalDrag.rangeEndMs);
    const isRange = finalDrag.rangeEndMs - finalDrag.rangeStartMs > SLOT_MS;

    let anchorRect = finalDrag.anchorRect;
    const escapeKey = (key: string) =>
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(key)
        : key.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const startKey = weekSlotKey(start, finalDrag.resourceId);
    const lastSlotStart = new Date(finalDrag.rangeEndMs - SLOT_MS);
    const endKey = weekSlotKey(lastSlotStart, finalDrag.resourceId);
    const startEl = document.querySelector(
      `[data-calendar-create-slot="${escapeKey(startKey)}"]`
    ) as HTMLElement | null;
    const endEl = isRange
      ? (document.querySelector(
          `[data-calendar-create-slot="${escapeKey(endKey)}"]`
        ) as HTMLElement | null)
      : null;
    if (startEl && endEl) {
      const a = startEl.getBoundingClientRect();
      const b = endEl.getBoundingClientRect();
      const top = Math.min(a.top, b.top);
      const left = Math.min(a.left, b.left);
      const right = Math.max(a.right, b.right);
      const bottom = Math.max(a.bottom, b.bottom);
      anchorRect = {
        top,
        left,
        width: right - left,
        height: bottom - top,
      };
    } else if (startEl) {
      const a = startEl.getBoundingClientRect();
      anchorRect = {
        top: a.top,
        left: a.left,
        width: a.width,
        height: a.height,
      };
    }

    if (isRange) {
      setCommittedSlotRange({
        resourceId: finalDrag.resourceId,
        startMs: finalDrag.rangeStartMs,
        endMs: finalDrag.rangeEndMs,
      });
    } else {
      setCommittedSlotRange(null);
    }

    slotDragSelectRef.current = null;
    setSlotDragSelect(null);

    if (!onTimeSlotClick) return;
    onTimeSlotClick(
      start,
      finalDrag.resourceId,
      anchorRect,
      isRange ? end : undefined
    );
  }, [onTimeSlotClick, updateSlotDragSelectFromPoint, clearHoldTimer, clearDocListeners, clearArmedScrollLock]);

  const beginSlotDragSelect = useCallback((
    e: React.PointerEvent<HTMLElement>,
    slotStart: Date,
    resourceId: string
  ) => {
    if (isDraggingMode) return;
    if (!onTimeSlotClick) return;
    if (e.button !== undefined && e.button !== 0) return;

    clearHoldTimer();
    clearArmedScrollLock();
    clearDocListeners();

    const rect = e.currentTarget.getBoundingClientRect();
    const originStartMs = slotStart.getTime();
    const originEndMs = originStartMs + SLOT_MS;
    const captureEl = e.currentTarget;
    const pointerId = e.pointerId;
    const next: SlotDragSelect = {
      pointerId,
      resourceId,
      originStartMs,
      originEndMs,
      rangeStartMs: originStartMs,
      rangeEndMs: originEndMs,
      originClientX: e.clientX,
      originClientY: e.clientY,
      phase: 'pending',
      hasDragged: false,
      captureEl,
      anchorRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
    };
    slotDragSelectRef.current = next;
    setSlotDragSelect(next);
    setCommittedSlotRange(null);

    holdTimerRef.current = window.setTimeout(() => {
      holdTimerRef.current = null;
      armSlotDragSelect();
    }, HOLD_TO_DRAG_MS);

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      updateSlotDragSelectFromPoint(ev.clientX, ev.clientY);
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      finishSlotDragSelect({
        pointerId: ev.pointerId,
        clientX: ev.clientX,
        clientY: ev.clientY,
        currentTarget: captureEl,
      });
    };
    const onCancel = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      cancelSlotDragSelect(pointerId);
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onCancel, true);
    docCleanupRef.current = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onCancel, true);
    };
  }, [
    isDraggingMode,
    onTimeSlotClick,
    clearHoldTimer,
    clearDocListeners,
    clearArmedScrollLock,
    armSlotDragSelect,
    updateSlotDragSelectFromPoint,
    finishSlotDragSelect,
    cancelSlotDragSelect,
  ]);

  // Clear committed range when create popover closes (parent clears highlight props)
  useEffect(() => {
    if (!highlightedRange && !highlightedSlotKey) {
      setCommittedSlotRange(null);
    }
  }, [highlightedRange, highlightedSlotKey]);

  const isSlotHighlighted = useCallback((slotStart: Date, resourceId: string) => {
    const drag = slotDragSelect;
    if (drag && drag.phase === 'armed' && drag.resourceId === resourceId) {
      return slotOverlapsRange(slotStart, drag.rangeStartMs, drag.rangeEndMs);
    }
    if (committedSlotRange && committedSlotRange.resourceId === resourceId) {
      return slotOverlapsRange(
        slotStart,
        committedSlotRange.startMs,
        committedSlotRange.endMs
      );
    }
    if (highlightedRange && highlightedRange.resourceId === resourceId) {
      return slotOverlapsRange(slotStart, highlightedRange.startMs, highlightedRange.endMs);
    }
    if (highlightedSlotKey && weekSlotKey(slotStart, resourceId) === highlightedSlotKey) {
      return true;
    }
    return false;
  }, [slotDragSelect, committedSlotRange, highlightedRange, highlightedSlotKey]);

  // Multi-slot create selection: hide per-slot "+" icons (one dashed range is enough).
  const hideCreatePlusIcons = Boolean(
    (slotDragSelect &&
      slotDragSelect.phase === 'armed' &&
      slotDragSelect.rangeEndMs - slotDragSelect.rangeStartMs > SLOT_MS) ||
      (committedSlotRange &&
        committedSlotRange.endMs - committedSlotRange.startMs > SLOT_MS) ||
      (highlightedRange && highlightedRange.endMs - highlightedRange.startMs > SLOT_MS)
  );

  // Resource popover state
  const [popoverResource, setPopoverResource] = useState<Resource | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverTriggerRef = useRef<HTMLDivElement | null>(null);

  // Close popover when clicking outside or scrolling — don't also create under the cursor
  const closeResourcePopover = useCallback(() => {
    setPopoverResource(null);
    setPopoverPosition(null);
  }, []);
  useDismissOverlayProtectingCalendarCreate(!!popoverResource, closeResourcePopover, [
    popoverRef,
    popoverTriggerRef,
  ]);

  useEffect(() => {
    if (!popoverResource) return;
    const handleScroll = () => {
      setPopoverResource(null);
      setPopoverPosition(null);
    };
    const scrollEl = contentAreaRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (scrollEl) scrollEl.removeEventListener('scroll', handleScroll);
    };
  }, [popoverResource]);

  const handleResourceHeaderClick = useCallback((resource: Resource, e: React.MouseEvent<HTMLDivElement>) => {
    if (resource.id === 'unassigned') return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const popoverWidth = 260; // approx width of popover
    const viewportWidth = window.innerWidth;
    // Center below the header cell, but clamp to stay within viewport
    let x = rect.left + rect.width / 2;
    const minX = popoverWidth / 2 + 12;
    const maxX = viewportWidth - popoverWidth / 2 - 12;
    x = Math.max(minX, Math.min(maxX, x));
    setPopoverPosition({
      x,
      y: rect.bottom + 6
    });
    setPopoverResource(prev => prev?.id === resource.id ? null : resource);
    popoverTriggerRef.current = e.currentTarget as HTMLDivElement;
  }, []);

  const handleViewDetails = useCallback(() => {
    if (popoverResource && onResourceClick) {
      onResourceClick(popoverResource);
    }
    setPopoverResource(null);
    setPopoverPosition(null);
  }, [popoverResource, onResourceClick]);

  // Calculate today's index in the week for horizontal scrolling (scroll to keep today in view)
  const todayIndex = useMemo(() => {
    const todayStart = startOfDay(new Date());
    return weekDays.findIndex(day => isSameDay(day, todayStart));
  }, [weekDays]);
  
  // Auto-scroll horizontally so today's date is in view (no vertical scroll)
  useLayoutEffect(() => {
    const currentWeekId = weekDays[0]?.toISOString() || null;

    if (isLoading) {
      setIsScrollReady(true);
      return;
    }

    if (scrolledWeekRef.current === currentWeekId) {
      setIsScrollReady(true);
      return;
    }

    const scrollToToday = () => {
      if (!contentAreaRef.current) {
        requestAnimationFrame(scrollToToday);
        return;
      }

      // Horizontal scroll only - scroll to today's column so today's date is in view
      if (todayIndex >= 0) {
        const dayColumnWidth = resources.length * 80;
        const horizontalScrollPosition = todayIndex * dayColumnWidth;
        contentAreaRef.current.scrollLeft = horizontalScrollPosition;
      }

      scrolledWeekRef.current = currentWeekId;
      setIsScrollReady(true);
    };

    scrollToToday();
  }, [isLoading, weekDays, todayIndex, resources.length]);
  
  // Reset scroll ready state when week changes
  useEffect(() => {
    const currentWeekId = weekDays[0]?.toISOString() || null;
    if (scrolledWeekRef.current !== currentWeekId) {
      setIsScrollReady(false);
    }
  }, [weekDays]);
  
  // Safety fallback: ensure content becomes visible even if scroll logic fails
  useEffect(() => {
    if (!isScrollReady) {
      const fallbackTimer = setTimeout(() => {
        setIsScrollReady(true);
      }, 500); // Show content after 500ms max
      return () => clearTimeout(fallbackTimer);
    }
  }, [isScrollReady]);

  // Combine regular bookings, time off requests, and field quotes, including buffer blocks
  const allBookings = useMemo(() => {
    const combined = [...bookings, ...timeOffRequests, ...fieldQuoteEvents];
    // Include buffer blocks in week view
    return combined;
  }, [bookings, timeOffRequests, fieldQuoteEvents]);

  // Debug logging - wrapped in try/catch to prevent errors from breaking render
  useEffect(() => {
    try {
      console.log('WeekView Render:', {
        weekDaysCount: weekDays.length,
        resourcesCount: resources.length,
        bookingsCount: bookings.length,
        timeOffCount: timeOffRequests.length,
        fieldQuotesCount: fieldQuoteEvents.length,
        allBookingsCount: allBookings.length,
        startHour,
        endHour,
        sampleBooking: allBookings[0] ? {
          id: allBookings[0].id,
          title: allBookings[0].title,
          start: allBookings[0].start,
          end: allBookings[0].end,
          resources: allBookings[0].extendedProps?.resources?.map((r: any) => ({ id: r.id, name: `${r.first_name} ${r.last_name}` })) || [],
          resourceId: allBookings[0].resourceId,
          extendedPropsResourceId: allBookings[0].extendedProps?.resource_id
        } : null,
        resources: resources.map(r => ({ id: r.id, name: `${r.first_name} ${r.last_name}` }))
      });
    } catch (error) {
      console.error('Error in WeekView debug logging:', error);
    }
  }, [weekDays.length, resources.length, bookings.length, timeOffRequests.length, fieldQuoteEvents.length, allBookings.length, startHour, endHour]);

  // Get unassigned bookings (bookings with no resources AND no assets)
  const hasUnassignedBookings = useMemo(() => {
    return allBookings.some(booking => {
      const hasResources = booking.extendedProps?.resources && booking.extendedProps.resources.length > 0;
      const hasAssets = booking.extendedProps?.booking_assets && booking.extendedProps.booking_assets.length > 0;
      return !hasResources && !hasAssets;
    });
  }, [allBookings]);

  // Current time indicator position
  const currentTimePosition = useMemo(() => {
    const now = new Date();
    const hour = now.getHours() + now.getMinutes() / 60;
    if (hour < startHour || hour > endHour) return null;
    return (hour - Math.floor(startHour)) * 60;
  }, [startHour, endHour]);

  // Calculate total height for the scrollable area
  // Each slot is 60px (now includes closing hour)
  const totalHeight = timeSlots.length * 60; // 60px per hour slot

  // No scroll sync needed since there's no internal scrollbar - content renders to full height

  // Debug: Log booking counts per resource
  useEffect(() => {
    if (allBookings.length > 0 && resources.length > 0 && weekDays.length > 0) {
      const firstDay = weekDays[0];
      resources.forEach(resource => {
        const resourceBookings = getBookingsForResourceAndDay(allBookings, resource.id, firstDay);
        if (resourceBookings.length > 0) {
          console.log(`Found ${resourceBookings.length} bookings for ${resource.first_name} ${resource.last_name} on ${format(firstDay, 'MMM d')}`);
        }
      });
    }
  }, [allBookings, resources, weekDays]);

  // Show loading state
  if (isLoading) {
    console.log('WeekView: Loading state');
    return (
      <div className="flex-1 flex items-center justify-center bg-white" style={{ height: 'calc(100vh - 200px)' }}>
        <div className="text-gray-500">Loading resources...</div>
      </div>
    );
  }

  // Show empty state if no resources
  if (resources.length === 0) {
    console.log('WeekView: No resources');
    return (
      <div className="flex-1 flex items-center justify-center bg-white" style={{ height: 'calc(100vh - 200px)' }}>
        <div className="text-gray-500 text-center">
          <p className="text-lg font-medium mb-2">No resources found</p>
          <p className="text-sm">Add resources to see them in the week view</p>
        </div>
      </div>
    );
  }

  console.log('WeekView: Rendering calendar with', {
    resourcesCount: resources.length,
    allBookingsCount: allBookings.length,
    weekDaysCount: weekDays.length
  });

  return (
    <div 
      ref={containerRef} 
      className="flex-1 bg-white" 
      style={{ 
        minHeight: 'calc(100vh - 200px)',
        // Hide content until scroll is complete to prevent visible scroll jump
        opacity: isScrollReady ? 1 : 0,
        transition: 'none' // No transition - instant show
      }}
    >
      <div className="flex relative" style={{ minHeight: `${totalHeight + 80}px` }}>
        {/* Time column - hidden since times show in each resource cell */}
        <div 
          ref={timeColumnRef}
          className="sticky left-0 z-10 bg-white border-r border-gray-200 flex-shrink-0 hidden" 
          style={{ width: '80px', height: '100%', overflow: 'hidden' }}
        >
          {/* Empty header for day names */}
          <div className="h-20 border-b border-gray-200 bg-gray-50"></div>
          {/* Time slots container - no transform needed since no scrolling */}
          <div 
            className="relative time-slots-container"
            style={{ 
              height: `${totalHeight}px`
            }}
          >
            {timeSlots.map((time, index) => {
              const hour = Math.floor(startHour) + index;
              
              // Calculate 30-minute time label (e.g., "9:30am" for 9am slot)
              let displayHalfHour = hour;
              let period = 'am';
              
              if (hour === 0) {
                displayHalfHour = 12;
                period = 'am';
              } else if (hour === 12) {
                displayHalfHour = 12;
                period = 'pm';
              } else if (hour > 12) {
                displayHalfHour = hour - 12;
                period = 'pm';
              } else if (hour < 12) {
                period = 'am';
              }
              
              const halfHourLabel = `${displayHalfHour}:30${period}`;
              
              return (
                <React.Fragment key={time}>
                  {/* Hour label container - contains both hour and 30-min label */}
                  <div
                    className="absolute left-0 right-0 border-b border-gray-100"
                    style={{ 
                      top: `${index * 60}px`,
                      height: '60px'
                    }}
                  >
                    {/* Hour label */}
                    <div className="flex items-start justify-end pr-2 pt-1 h-full">
                      <span className="text-xs text-gray-500 font-medium">{time}</span>
                    </div>
                    {/* 30-minute label - only show if not the last slot (closing hour) */}
                    {index < timeSlots.length - 1 && (
                      <div
                        className="absolute right-0 flex items-center justify-end pr-2"
                        style={{ 
                          top: '30px',
                          height: '30px',
                          width: '100%',
                          pointerEvents: 'none'
                        }}
                      >
                        <span 
                          className="text-[10px] text-gray-500 font-normal"
                          style={{ 
                            display: 'block',
                            lineHeight: '1.2'
                          }}
                        >
                          {halfHourLabel}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* 30-minute marker - dotted line */}
                  <div
                    key={`time-half-${index}`}
                    className="absolute left-0 right-0 border-b border-dotted"
                    style={{ 
                      top: `${index * 60 + 30}px`,
                      height: '0px',
                      borderBottomWidth: '1px',
                      borderBottomColor: '#E5E7EB',
                      pointerEvents: 'none'
                    }}
                  />
                </React.Fragment>
              );
            })}
            {/* Closing hour line is now part of the last slot, so no separate line needed */}
          </div>
        </div>

        {/* Days and resources grid - Horizontal scroll only, no vertical scroll */}
        <div 
          ref={contentAreaRef}
          className="flex-1 overflow-x-auto overflow-y-visible"
          style={{ minHeight: `${totalHeight + 80}px` }}
        >
          {/* Day headers row - sticky */}
          <div
            data-calendar-grid-header
            className="flex sticky top-0 z-20 bg-white border-b-2 border-gray-300"
          >
            {weekDays.map((day, dayIndex) => {
              const isToday = isSameDay(day, today);
              return (
                <div
                  key={dayIndex}
                  className="flex-shrink-0"
                  style={{ 
                    width: `${resources.length * 80}px`, 
                    minWidth: `${resources.length * 80}px`,
                    borderRight: dayIndex < weekDays.length - 1 ? '2px solid #D1D5DB' : 'none',
                    backgroundColor: isToday ? '#EFF6FF' : 'transparent'
                  }}
                >
                  {/* Main day header */}
                  <div className="h-12 border-b border-gray-200 flex flex-col items-center justify-center bg-gray-50">
                    <div className={`text-xs font-semibold ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                      {format(day, 'EEE')}
                    </div>
                    <div className={`text-sm font-bold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                      {format(day, 'MMM d')}
                    </div>
                  </div>
                  {/* Resource column headers under each day */}
                  <div className="h-8 border-b border-gray-200 bg-white flex">
                    {resources.map((resource, resIndex) => {
                      const isUnassigned = resource.id === 'unassigned';
                      const isAsset = !!(resource as any)._isAsset;
                      const assetInitials = (() => {
                        if (!isAsset) return '';
                        const name = (resource.first_name || '').trim();
                        const parts = name.split(/\s+/);
                        if (parts.length >= 2) {
                          return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
                        }
                        return (name[0] || 'A').toUpperCase();
                      })();
                      const initials = isUnassigned ? 'U' : isAsset ? assetInitials : `${resource.first_name?.[0] || ''}${resource.last_name?.[0] || ''}`;
                      const isMobile = !isAsset && Boolean(resource.is_mobile_available);
                      
                      return (
                        <div
                          key={`header-${dayIndex}-${resource.id}`}
                          className={`flex-1 border-r border-gray-100 flex items-center justify-center gap-1.5 ${!isUnassigned ? 'cursor-pointer active:bg-gray-50 active:scale-95 transition-all duration-150' : ''}`}
                          style={{ 
                            minWidth: '80px',
                            maxWidth: '80px',
                            width: '80px'
                          }}
                          onClick={(e) => !isUnassigned && handleResourceHeaderClick(resource, e)}
                        >
                          {isUnassigned ? (
                            <span className="text-xs font-semibold text-gray-600">
                              Unassigned
                            </span>
                          ) : isAsset ? (
                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-[10px] font-semibold flex-shrink-0">
                              {initials}
                            </div>
                          ) : (
                            <>
                    {resource.avatar_url ? (
                            <>
                      <img
                        src={resource.avatar_url}
                        alt={`${resource.first_name} ${resource.last_name}`}
                        className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    const fallback = parent.querySelector('.resource-initials-badge') as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                  }
                                }}
                              />
                              <div className="resource-initials-badge hidden items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] font-semibold flex-shrink-0">
                                {initials}
                              </div>
                            </>
                          ) : (
                            <div className="resource-initials-badge flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] font-semibold flex-shrink-0">
                              {initials}
                      </div>
                    )}
                          {isMobile && (
                            <MapPin 
                              className="w-4 h-4 text-blue-600 flex-shrink-0" 
                              style={{ display: 'block', visibility: 'visible', opacity: 1 }}
                              title="Mobile Resource"
                            />
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
                </div>

          {/* Main content area - one row for all resources */}
          <div className="relative" style={{ height: `${totalHeight}px`, minHeight: `${totalHeight}px` }}>
            {/* Days columns */}
                {weekDays.map((day, dayIndex) => {
                  const isToday = isSameDay(day, today);
                  
                  return (
                    <div
                  key={dayIndex}
                  className="absolute"
                  style={{ 
                    left: `${dayIndex * (resources.length * 80)}px`,
                    top: '0',
                    width: `${resources.length * 80}px`,
                    height: `${totalHeight}px`,
                    backgroundColor: dayIndex % 2 === 0 ? '#FFFFFF' : '#F9FAFB'
                  }}
                >
                  {/* Day divider - positioned on the right edge */}
                  {dayIndex < weekDays.length - 1 && (
                    <div
                      className="absolute top-0 bottom-0"
                      style={{
                        right: '-1px',
                        width: '2px',
                        backgroundColor: '#9CA3AF',
                        zIndex: 5
                      }}
                    />
                  )}
                  {/* Resource columns within this day */}
                  {resources.map((resource, resIndex) => {
                    const resourceBookings = getBookingsForResourceAndDay(allBookings, resource.id, day);
                    // Calculate column assignments for overlapping bookings
                    const columnAssignments = assignBookingColumns(resourceBookings, day);
                    
                    // Get resource availability for this resource
                    const resourceAvailability = resourceAvailabilities.get(resource.id) || null;
                    
                    return (
                      <div
                        key={`content-${dayIndex}-${resource.id}`}
                        className="absolute"
                        style={{ 
                          left: `${resIndex * 80}px`,
                          top: '0',
                          width: '80px',
                          height: `${totalHeight}px`,
                          backgroundColor: 'transparent',
                          borderRight: resIndex < resources.length - 1 ? '1px solid #E5E7EB' : 'none'
                        }}
                    >
                      {/* Time slot grid background with time labels */}
                      {timeSlots.map((time, timeIndex) => {
                        const hour = Math.floor(startHour) + timeIndex;
                        const period = hour >= 12 ? 'p' : 'a';
                        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        const hourLabel = `${displayHour}:00${period}`;
                        const halfLabel = `${displayHour}:30${period}`;
                        return (
                        <div
                          key={time}
                          className="absolute left-0 right-0 border-b border-gray-100"
                          style={{ 
                              top: `${timeIndex * 60}px`,
                              height: '60px',
                              pointerEvents: 'none'
                            }}
                        >
                          <span className="absolute top-0 left-0.5 text-[7px] text-gray-400 leading-none select-none">{hourLabel}</span>
                          <span className="absolute top-[30px] left-0.5 text-[7px] text-gray-400 leading-none select-none">{halfLabel}</span>
                          </div>
                        );
                        })}
                        {/* Greyed-out unavailable time slots */}
                        {timeSlots.map((time, timeIndex) => {
                          const hour = Math.floor(startHour) + timeIndex;
                          // Skip closing hour (last slot)
                          if (hour > Math.ceil(endHour)) return null;
                          
                          // Get resource's earliest start time for this day
                          const resourceHours = getResourceWorkingHoursForDay(day, resourceAvailability);
                          const resourceStartHour = resourceHours ? 
                            resourceHours.startTime.getHours() + resourceHours.startTime.getMinutes() / 60 : null;
                          
                          // Calculate what portion of this hour should be blocked
                          const isFirstHour = hour === Math.floor(startHour);
                          const businessPartialStart = isFirstHour && startHour > hour;
                          const resourcePartialStart = resourceStartHour !== null && hour === Math.floor(resourceStartHour) && resourceStartHour > hour;
                          
                          // Determine the actual start time for blocking (use the later of business hours or resource start)
                          const actualStartHour = resourceStartHour !== null && resourceStartHour > startHour 
                            ? resourceStartHour 
                            : startHour;
                          const hasPartialStart = isFirstHour && actualStartHour > hour;
                          
                          // Check availability at 30-minute granularity
                          // We need to check the full hour, but account for partial starts
                          const hourStart = hour;
                          const hourEnd = hour + 1;
                          
                          // Check each 30-minute block within this hour
                          // First half: hour to hour+0.5 (e.g., 6:00-6:30)
                          const firstHalfStart = hourStart;
                          const firstHalfEnd = hourStart + 0.5;
                          
                          // Second half: hour+0.5 to hour+1 (e.g., 6:30-7:00)
                          const secondHalfStart = hourStart + 0.5;
                          const secondHalfEnd = hourEnd;
                          
                          // Check if blocks are before the actual start time
                          const firstHalfBeforeStart = firstHalfEnd <= actualStartHour;
                          const secondHalfBeforeStart = secondHalfEnd <= actualStartHour;
                          
                          // Check if this hour is available for this resource on this day (for overall availability check)
                          // For unassigned resource, all time slots are available (no grey blocks)
                          const isUnassignedResource = resource.id === 'unassigned';
                          const isAvailable = isUnassignedResource ? true : isTimeSlotAvailable(hour, day, resourceAvailability, startHour);
                          
                          // Check coverage for 30-minute blocks (always check, even if hour is not available)
                          // Check coverage only for blocks that are at or after the actual start time
                          const firstHalfCovered = !firstHalfBeforeStart && 
                            isTimeRangeCovered(firstHalfStart, firstHalfEnd, day, resourceAvailability);
                          const secondHalfCovered = !secondHalfBeforeStart && 
                            isTimeRangeCovered(secondHalfStart, secondHalfEnd, day, resourceAvailability);
                          
                          // Skip all grey blocks for unassigned resource
                          if (isUnassignedResource) {
                            return null;
                          }
                          
                          // If not available at all, grey out the entire hour
                          // When hour is not available, always block the full hour regardless of partial starts
                          if (!isAvailable) {
                            return (
                              <div
                                key={`unavailable-${timeIndex}`}
                                className="absolute left-0 right-0 bg-gray-300"
                                style={{
                                  top: `${timeIndex * 60}px`,
                                  height: '60px',
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                  opacity: 0.7
                                }}
                              />
                            );
                          }
                          
                          // Hour is available, but check 30-minute blocks for partial coverage
                          // Render blocks for uncovered portions
                          const blocks: JSX.Element[] = [];
                          
                          // Block first half if before start OR not covered
                          if (firstHalfStart < firstHalfEnd && (firstHalfBeforeStart || !firstHalfCovered)) {
                            blocks.push(
                              <div
                                key={`unavailable-first-half-${timeIndex}`}
                                className="absolute left-0 right-0 bg-gray-300"
                                style={{
                                  top: `${(firstHalfStart - hour) * 60}px`,
                                  height: `${(firstHalfEnd - firstHalfStart) * 60}px`,
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                  opacity: 0.7
                                }}
                              />
                            );
                          }
                          
                          // Block second half if before start OR not covered
                          if (secondHalfStart < secondHalfEnd && (secondHalfBeforeStart || !secondHalfCovered)) {
                            blocks.push(
                              <div
                                key={`unavailable-second-half-${timeIndex}`}
                                className="absolute left-0 right-0 bg-gray-300"
                                style={{
                                  top: `${(secondHalfStart - hour) * 60}px`,
                                  height: `${(secondHalfEnd - secondHalfStart) * 60}px`,
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                  opacity: 0.7
                                }}
                              />
                            );
                          }
                          
                          return blocks.length > 0 ? <React.Fragment key={`unavailable-blocks-${timeIndex}`}>{blocks}</React.Fragment> : null;
                        })}
                        {/* 30-minute markers - dotted lines */}
                        {timeSlots.map((time, timeIndex) => (
                          <div
                            key={`half-${timeIndex}`}
                            className="absolute left-0 right-0 border-b border-dotted"
                            style={{ 
                              top: `${timeIndex * 60 + 30}px`,
                              height: '0px',
                              borderBottomWidth: '1px',
                              borderBottomColor: '#E5E7EB',
                            pointerEvents: 'none'
                          }}
                        />
                      ))}
                        {/* Hoverable 30-minute time slots - only show for available hours */}
                        {timeSlots.map((time, timeIndex) => {
                          // Create two 30-minute blocks per hour
                          const hour = Math.floor(startHour) + timeIndex;
                          const hourDecimal = startHour + timeIndex;
                          
                          // Check if we're past the closing hour (don't show hoverable slots for closing hour itself)
                          if (hourDecimal > endHour) return null;
                          
                          // Don't show hoverable slots for the closing hour, just the label
                          const isClosingHour = hourDecimal >= endHour;
                          
                          // Get resource's actual start time for this day
                          const resourceHours = getResourceWorkingHoursForDay(day, resourceAvailability);
                          const resourceStartHour = resourceHours ? 
                            resourceHours.startTime.getHours() + resourceHours.startTime.getMinutes() / 60 : null;
                          
                          // Determine the actual start time (use the later of business hours or resource start)
                          const isFirstHour = hour === Math.floor(startHour);
                          const actualStartHour = resourceStartHour !== null && resourceStartHour > startHour
                            ? (isFirstHour && resourceStartHour > hour ? resourceStartHour : hour)
                            : (isFirstHour && startHour > hour ? startHour : hour);
                          
                          const block1Start = new Date(day);
                          const block1StartHour = Math.floor(actualStartHour);
                          const block1StartMin = Math.round((actualStartHour - block1StartHour) * 60);
                          block1Start.setHours(block1StartHour, block1StartMin, 0, 0);
                          
                          const block1End = new Date(day);
                          block1End.setHours(block1StartHour, block1StartMin + 30, 0, 0);
                          
                          const block2Start = new Date(day);
                          block2Start.setHours(block1StartHour, block1StartMin + 30, 0, 0);
                          
                          const block2End = new Date(day);
                          block2End.setHours(block1StartHour + 1, block1StartMin, 0, 0);
                          
                          // Check if this hour is available for this resource on this day
                          const isAvailable = isTimeSlotAvailable(hour, day, resourceAvailability, startHour);
                          
                          // Don't show hoverable slots if not available
                          if (!isAvailable) return null;
                          
                          // Check if resource starts partway through this hour
                          const resourceStartsInThisHour = resourceStartHour !== null && 
                            hour === Math.floor(resourceStartHour) && 
                            resourceStartHour > hour;
                          const businessStartsInThisHour = isFirstHour && startHour > hour;
                          const hasPartialStart = resourceStartsInThisHour || businessStartsInThisHour;
                          
                          // Determine which blocks to show based on resource/business start time
                          let showFirstBlock = true;
                          let showSecondBlock = !isClosingHour;
                          
                          if (resourceStartsInThisHour && resourceStartHour !== null) {
                            // Resource starts partway through this hour
                            if (resourceStartHour >= hour + 0.5) {
                              // Resource starts at or after 30 minutes (e.g., 6:30am in 6am slot)
                              // Don't show first block (6:00-6:30), only show second block starting at resource start (6:30-7:00)
                              showFirstBlock = false;
                            } else {
                              // Resource starts in first 30 minutes (e.g., 6:15am in 6am slot)
                              // Show first block starting at resource start
                              showFirstBlock = true;
                            }
                          } else if (businessStartsInThisHour) {
                            // Business hours start partway through, but resource starts at or before business start
                            showFirstBlock = startHour <= hour + 0.5;
                          }
                          
                          // Calculate positioning for partial hour start
                          const effectiveStartHour = resourceStartsInThisHour && resourceStartHour !== null && resourceStartHour > startHour
                            ? resourceStartHour
                            : (businessStartsInThisHour ? startHour : hour);
                          
                          const firstBlockTop = (effectiveStartHour > hour)
                            ? (effectiveStartHour - hour) * 60 
                            : timeIndex * 60;
                          const firstBlockHeight = (effectiveStartHour > hour)
                            ? Math.min(30, (hour + 1 - effectiveStartHour) * 60)
                            : 30;
                          
                          // Update block times to start at the effective start time
                          if (effectiveStartHour > hour) {
                            const effectiveStartHourInt = Math.floor(effectiveStartHour);
                            const effectiveStartMin = Math.round((effectiveStartHour - effectiveStartHourInt) * 60);
                            
                            if (showFirstBlock) {
                              // First block starts at effective start time
                              block1Start.setHours(effectiveStartHourInt, effectiveStartMin, 0, 0);
                              block1End.setHours(effectiveStartHourInt, effectiveStartMin + 30, 0, 0);
                              block2Start.setHours(effectiveStartHourInt, effectiveStartMin + 30, 0, 0);
                              block2End.setHours(effectiveStartHourInt + 1, effectiveStartMin, 0, 0);
                            } else {
                              // First block is hidden, second block starts at effective start time (e.g., 6:30-7:00)
                              block2Start.setHours(effectiveStartHourInt, effectiveStartMin, 0, 0);
                              block2End.setHours(effectiveStartHourInt, effectiveStartMin + 30, 0, 0);
                            }
                          }
                          
                          const block1Highlighted = isSlotHighlighted(block1Start, resource.id);
                          const block2Highlighted = isSlotHighlighted(block2Start, resource.id);
                          const idleBg = isDraggingMode
                            ? 'rgba(59, 130, 246, 0.05)'
                            : 'transparent';
                          const hoverBg = isDraggingMode
                            ? 'rgba(59, 130, 246, 0.2)'
                            : 'rgba(59, 130, 246, 0.1)';
                          const activeBg = 'rgba(59, 130, 246, 0.18)';
                          const bindEmptySlotHandlers = (slotStart: Date) => ({
                            onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
                              if (isDraggingMode && onDropBooking) return;
                              // Don't preventDefault — allow scroll until hold arms drag-select
                              beginSlotDragSelect(e, slotStart, resource.id);
                            },
                            onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
                              if (!slotDragSelectRef.current || slotDragSelectRef.current.pointerId !== e.pointerId) {
                                return;
                              }
                              updateSlotDragSelectFromPoint(e.clientX, e.clientY);
                            },
                            onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => {
                              if (isDraggingMode && onDropBooking) {
                                onDropBooking(slotStart, resource.id);
                                return;
                              }
                              finishSlotDragSelect(e);
                            },
                            onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => {
                              cancelSlotDragSelect(e.pointerId);
                            },
                            onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                          });

                          return (
                            <React.Fragment key={`slots-${timeIndex}`}>
                              {/* First 30-minute block (0-30) */}
                              {showFirstBlock && showSecondBlock && (
                                <div
                                  data-calendar-create-slot={weekSlotKey(block1Start, resource.id)}
                                  data-slot-resource={resource.id}
                                  data-slot-start-ms={String(block1Start.getTime())}
                                  className={`absolute left-0 right-0 cursor-pointer group transition-colors duration-150 ${
                                    isDraggingMode || block1Highlighted
                                      ? 'border-2 border-dashed border-blue-400'
                                      : ''
                                  }`}
                                  style={{
                                    top: `${firstBlockTop}px`,
                                    height: `${firstBlockHeight}px`,
                                    zIndex: 2,
                                    backgroundColor: block1Highlighted ? activeBg : idleBg,
                                    // Keep pan-* stable mid-gesture — flipping to none on arm cancels the pointer on iOS.
                                    // Armed scroll lock is handled via non-passive touchmove preventDefault.
                                    touchAction: 'pan-x pan-y',
                                    userSelect: 'none',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!block1Highlighted) {
                                      e.currentTarget.style.backgroundColor = hoverBg;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = block1Highlighted
                                      ? activeBg
                                      : idleBg;
                                  }}
                                  {...bindEmptySlotHandlers(block1Start)}
                                >
                                  <div
                                    className={`absolute inset-0 flex items-center justify-center transition-opacity pointer-events-none ${
                                      hideCreatePlusIcons && block1Highlighted
                                        ? 'opacity-0'
                                        : block1Highlighted
                                          ? 'opacity-100'
                                          : 'opacity-0 group-hover:opacity-100'
                                    }`}
                                  >
                                    {isDraggingMode ? (
                                      <div className="bg-green-500 rounded-full p-1.5 shadow-sm">
                                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    ) : (
                                    <div className="bg-blue-500 rounded-full p-1.5 shadow-sm">
                                      <Plus className="h-4 w-4 text-white" />
                                    </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Second 30-minute block (30-60) */}
                              {showSecondBlock && (
                                <div
                                  data-calendar-create-slot={weekSlotKey(block2Start, resource.id)}
                                  data-slot-resource={resource.id}
                                  data-slot-start-ms={String(block2Start.getTime())}
                                  className={`absolute left-0 right-0 cursor-pointer group transition-colors duration-150 ${
                                    isDraggingMode || block2Highlighted
                                      ? 'border-2 border-dashed border-blue-400'
                                      : ''
                                  }`}
                                  style={{
                                    top: `${showFirstBlock ? timeIndex * 60 + 30 : firstBlockTop}px`,
                                    height: '30px',
                                    zIndex: 2,
                                    backgroundColor: block2Highlighted ? activeBg : idleBg,
                                    touchAction: 'pan-x pan-y',
                                    userSelect: 'none',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!block2Highlighted) {
                                      e.currentTarget.style.backgroundColor = hoverBg;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = block2Highlighted
                                      ? activeBg
                                      : idleBg;
                                  }}
                                  {...bindEmptySlotHandlers(block2Start)}
                                >
                                  <div
                                    className={`absolute inset-0 flex items-center justify-center transition-opacity pointer-events-none ${
                                      hideCreatePlusIcons && block2Highlighted
                                        ? 'opacity-0'
                                        : block2Highlighted
                                          ? 'opacity-100'
                                          : 'opacity-0 group-hover:opacity-100'
                                    }`}
                                  >
                                    {isDraggingMode ? (
                                      <div className="bg-green-500 rounded-full p-1.5 shadow-sm">
                                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    ) : (
                                    <div className="bg-blue-500 rounded-full p-1.5 shadow-sm">
                                      <Plus className="h-4 w-4 text-white" />
                                    </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* Closing hour is now included in the slots, so no separate line needed */}

                      {/* Current time indicator */}
                      {currentTimePosition !== null && isToday && (
                        <div
                          className="absolute left-0 right-0 z-30 pointer-events-none"
                          style={{ top: `${currentTimePosition}px` }}
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-red-500 rounded-full -ml-1"></div>
                            <div className="flex-1 h-0.5 bg-red-500"></div>
                          </div>
                        </div>
                      )}

                      {/* Booking blocks for this resource and day */}
                      {resourceBookings.map((booking) => {
                          const isBufferBlock = booking.extendedProps?.is_buffer_block === true;
                          
                          // Debug logging for buffer blocks
                          if (isBufferBlock) {
                            console.log('🔍 Buffer block in WeekView:', {
                              bookingId: booking.id,
                              resourceId: resource.id,
                              bookingResourceId: booking.resourceId,
                              extendedPropsResourceId: booking.extendedProps?.resource_id,
                              day: format(day, 'MMM d'),
                              bookingStart: booking.start,
                              bookingEnd: booking.end,
                              startHour,
                              endHour
                            });
                          }
                          
                          const position = calculateBookingPosition(booking, day, startHour, endHour, resourceAvailability);
                          
                          if (!position.visible) {
                            // Log buffer blocks that aren't visible
                            if (isBufferBlock) {
                              console.warn('⚠️ Buffer block not visible:', {
                                bookingId: booking.id,
                                bookingTitle: booking.title,
                                day: format(day, 'MMM d'),
                                startHour,
                                endHour,
                                bookingStart: booking.start,
                                bookingEnd: booking.end,
                                position,
                                startHourDecimal: parseISO(booking.start).getHours() + parseISO(booking.start).getMinutes() / 60,
                                endHourDecimal: parseISO(booking.end).getHours() + parseISO(booking.end).getMinutes() / 60
                              });
                            } else if (resourceBookings.indexOf(booking) < 2) {
                              console.log('Booking not visible:', {
                                bookingId: booking.id,
                                bookingTitle: booking.title,
                                day: format(day, 'MMM d'),
                                startHour,
                                endHour,
                                bookingStart: booking.start,
                                bookingEnd: booking.end,
                                position
                              });
                            }
                            return null;
                          }

                          // Get column assignment for this booking
                          const assignment = columnAssignments.get(booking.id);
                          const column = assignment?.column || 0;
                          const totalColumns = assignment?.totalColumns || 1;
                          
                          // Calculate width and left position for side-by-side display
                          const bookingWidth = totalColumns > 1 ? (76 / totalColumns) : 76; // 76px = 80px - 4px (2px margin on each side)
                          const bookingLeft = totalColumns > 1 ? (2 + column * (76 / totalColumns)) : 2;

                        const isTimeOff = booking.extendedProps.is_time_off;
                        const isDraggedBooking = draggedBookingId === booking.id;
                        const bgColor = isDraggedBooking
                          ? 'rgba(59, 130, 246, 0.3)' // Faded blue when being dragged
                          : isTimeOff 
                          ? '#FEF3C7' // Light yellow for time off
                            : isBufferBlock
                            ? '#000000' // Black for buffer blocks
                          : booking.backgroundColor || '#3B82F6';
                          const textColor = isTimeOff 
                            ? '#92400E' 
                            : isBufferBlock 
                            ? '#FFFFFF' // White text on black background
                            : '#FFFFFF';

                        return (
                          <div
                              key={`${booking.id}-${dayIndex}-${resource.id}`}
                              className={`absolute rounded-md shadow-sm pointer-events-auto cursor-pointer hover:shadow-md transition-all ${
                                isDraggedBooking ? 'opacity-50 border-4 border-blue-500 border-dashed' : ''
                              }`}
                            style={{
                                left: `${bookingLeft}px`,
                                width: `${bookingWidth}px`,
                              top: `${position.top}px`,
                                height: `${Math.max(position.height, 20)}px`,
                              backgroundColor: bgColor,
                              color: textColor,
                                zIndex: isDraggedBooking ? 20 : 15,
                                overflow: 'hidden'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onBookingClick({ event: { ...booking, jsEvent: e } });
                            }}
                          >
                              <div className="p-0.5 h-full flex flex-col justify-start overflow-hidden gap-0.5">
                                {/* Booking # and Mobile Badge */}
                                <div className="flex items-center gap-1">
                                  {booking.booking_number > 0 && (
                                    <div className="text-[8px] font-semibold opacity-90 truncate leading-tight whitespace-nowrap">
                                      #{booking.booking_number}
                                    </div>
                                  )}
                                  {booking.extendedProps?.mobile_booking === true && (
                                    <MapPin className="h-3 w-3 text-white opacity-90 flex-shrink-0" />
                                  )}
                                </div>
                                {/* Title */}
                                <div className="text-[9px] font-semibold truncate leading-tight whitespace-nowrap">
                                {booking.title}
                              </div>
                                {/* Customer Name */}
                                {booking.extendedProps?.customer && (
                                  <div className="text-[8px] opacity-90 truncate leading-tight whitespace-nowrap">
                                    {booking.extendedProps.customer}
                                </div>
                              )}
                                {/* Assigned resources/assets badges */}
                                {(() => {
                                  const isAssetColumn = !!(resource as any)._isAsset;
                                  const bookingResources = booking.extendedProps?.resources || [];
                                  const bookingAssets = booking.extendedProps?.booking_assets || [];
                                  // When on an asset column, show assigned people; on a resource column, show assigned assets
                                  const badgeItems = isAssetColumn
                                    ? bookingResources.map((r: any) => ({
                                        id: r.id,
                                        label: `${(r.first_name || '').charAt(0)}${(r.last_name || '').charAt(0)}`,
                                        avatar: r.avatar_url,
                                        isAsset: false
                                      }))
                                    : bookingAssets.map((a: any) => {
                                        const name = a.name || 'A';
                                        const parts = name.trim().split(/\s+/);
                                        return {
                                          id: a.id || a.asset_id,
                                          label: parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase(),
                                          avatar: a.image_url,
                                          isAsset: true
                                        };
                                      });
                                  if (badgeItems.length === 0) return null;
                                  const visible = badgeItems.slice(0, 3);
                                  const extra = badgeItems.length - visible.length;
                                  return (
                                    <div className="flex items-center mt-0.5">
                                      {visible.map((item: any, idx: number) => (
                                        <div
                                          key={item.id || idx}
                                          className="rounded-full overflow-hidden flex items-center justify-center"
                                          style={{
                                            width: 14,
                                            height: 14,
                                            marginLeft: idx === 0 ? 0 : -4,
                                            zIndex: 10 - idx,
                                            backgroundColor: item.isAsset ? 'rgba(249,115,22,0.9)' : 'rgba(255,255,255,0.85)',
                                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                                          }}
                                        >
                                          {item.avatar ? (
                                            <img src={item.avatar} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <span className={`text-[6px] font-semibold ${item.isAsset ? 'text-white' : 'text-gray-800'}`} style={{ lineHeight: 1 }}>
                                              {item.label}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                      {extra > 0 && (
                                        <div
                                          className="rounded-full text-white flex items-center justify-center text-[6px] font-semibold"
                                          style={{ width: 14, height: 14, marginLeft: -4, backgroundColor: 'rgba(17,24,39,0.85)', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }}
                                        >
                                          +{extra}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                {/* Start Time and End Time */}
                                {position.height > 30 && (
                                  <>
                                    {/* Start Time - show if this is the start day */}
                                    {format(parseISO(booking.start), 'MMM d') === format(day, 'MMM d') && (
                                      <div className="text-[8px] opacity-90 truncate leading-tight whitespace-nowrap">
                                        Start: {format(parseISO(booking.start), 'h:mm a')}
                        </div>
                      )}
                                    {/* End Time - show if this is the end day, or if it's the start day and booking is same day */}
                                    {(format(parseISO(booking.end), 'MMM d') === format(day, 'MMM d') || 
                                      (format(parseISO(booking.start), 'MMM d') === format(day, 'MMM d') && 
                                       format(parseISO(booking.start), 'MMM d') === format(parseISO(booking.end), 'MMM d'))) && (
                                      <div className="text-[8px] opacity-90 truncate leading-tight whitespace-nowrap">
                                        End: {format(parseISO(booking.end), 'h:mm a')}
                                </div>
                                    )}
                                  </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {/* Google Calendar events for this resource and day */}
                      {googleCalendarEvents
                        .filter(ge => {
                          if (!ge.start_time) return false;
                          const geStart = parseISO(ge.start_time);
                          const isThisDay = isSameDay(geStart, day);
                          const isForResource = !ge.resource_id || ge.resource_id === resource.id;
                          const isUnassignedRow = resource.id === 'unassigned';
                          return isThisDay && (isForResource || (isUnassignedRow && !ge.resource_id));
                        })
                        .map(ge => {
                          if (!ge.start_time || !ge.end_time) return null;
                          const geStart = parseISO(ge.start_time);
                          const geEnd = parseISO(ge.end_time);
                          const startDecimal = geStart.getHours() + geStart.getMinutes() / 60;
                          const endDecimal = geEnd.getHours() + geEnd.getMinutes() / 60;
                          // All-day events fill the visible day instead of a sliver at midnight.
                          const top = ge.all_day ? 0 : (startDecimal - startHour) * 60;
                          const height = ge.all_day ? totalHeight : Math.max((endDecimal - startDecimal) * 60, 16);
                          if (top + height < 0 || top > totalHeight) return null;
                          return (
                            <div
                              key={`gcal-${ge.id}`}
                              className="absolute rounded-md shadow-sm pointer-events-auto cursor-pointer border-l-2 border-blue-400 hover:shadow-md transition-shadow"
                              style={{
                                top: `${Math.max(top, 0)}px`,
                                height: `${height}px`,
                                left: '2px',
                                width: '76px',
                                backgroundColor: 'rgba(66, 133, 244, 0.15)',
                                zIndex: 5,
                                overflow: 'hidden',
                                padding: '2px 4px',
                              }}
                              onClick={() => onGcalEventClick?.(ge)}
                              title={ge.description || ge.title || ''}
                            >
                              <div className="flex items-center gap-0.5">
                                <svg className="h-2.5 w-2.5 flex-shrink-0 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                                  <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                </svg>
                                <span className="text-[8px] font-medium text-blue-700 truncate">{ge.title || '(No title)'}</span>
                              </div>
                              <div className="text-[7px] text-blue-500 truncate mt-0.5">
                                {ge.all_day ? 'All day' : `${format(geStart, 'h:mm a')} - ${format(geEnd, 'h:mm a')}`}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Resource Info Popover - Speech Bubble */}
      {popoverResource && popoverPosition && (
        <>
          {/* Invisible backdrop for mobile tap-to-dismiss */}
          <div
            className="fixed inset-0 z-[99998]"
            onClick={() => { setPopoverResource(null); setPopoverPosition(null); }}
          />
          <div
            ref={popoverRef}
            className="fixed z-[99999] animate-popover-in"
            style={{
              left: `${popoverPosition.x}px`,
              top: `${popoverPosition.y}px`,
            }}
          >
            {/* Speech bubble arrow */}
            <div
              className="absolute -top-[6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-l border-t border-gray-200"
              style={{ zIndex: 1 }}
            />
            {/* Popover card */}
            <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" style={{ minWidth: '220px', maxWidth: '280px' }}>
              {/* Close button */}
              <button
                onClick={() => { setPopoverResource(null); setPopoverPosition(null); }}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors z-10"
              >
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>

              <div className="p-4 pt-3.5">
                {/* Avatar + Name */}
                <div className="flex items-center gap-3 mb-3">
                  {(popoverResource as any)._isAsset ? (
                    <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0 ring-2 ring-orange-100">
                      {(() => {
                        const name = (popoverResource.first_name || '').trim();
                        const parts = name.split(/\s+/);
                        if (parts.length >= 2) return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
                        return (name[0] || 'A').toUpperCase();
                      })()}
                    </div>
                  ) : popoverResource.avatar_url ? (
                    <img
                      src={popoverResource.avatar_url}
                      alt={`${popoverResource.first_name} ${popoverResource.last_name}`}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-blue-100"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0 ring-2 ring-blue-100">
                      {popoverResource.first_name?.[0] || ''}{popoverResource.last_name?.[0] || ''}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {popoverResource.first_name}{popoverResource.last_name ? ` ${popoverResource.last_name}` : ''}
                    </div>
                    {(popoverResource as any)._isAsset ? (
                      (popoverResource as any)._assetType && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Package className="w-3 h-3 text-orange-500" />
                          <span className="text-[10px] text-orange-500 font-medium capitalize">{(popoverResource as any)._assetType}</span>
                        </div>
                      )
                    ) : (
                      popoverResource.is_mobile_available && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 text-blue-500" />
                          <span className="text-[10px] text-blue-500 font-medium">Mobile</span>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Contact Info — only for team members */}
                {!(popoverResource as any)._isAsset && (
                  <div className="space-y-1.5 mb-3">
                    {popoverResource.email && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{popoverResource.email}</span>
                      </div>
                    )}
                    {popoverResource.phone && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{popoverResource.phone}</span>
                      </div>
                    )}
                    {!popoverResource.email && !popoverResource.phone && (
                      <div className="flex items-center gap-2 text-xs text-gray-400 italic">
                        <User className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>No contact info</span>
                      </div>
                    )}
                  </div>
                )}

                {/* View Details Button */}
                <button
                  onClick={handleViewDetails}
                  className={`w-full flex items-center justify-center gap-1.5 text-white text-xs font-semibold py-2.5 rounded-xl transition-all duration-150 active:scale-[0.97] ${
                    (popoverResource as any)._isAsset
                      ? 'bg-orange-500 hover:bg-orange-600 active:bg-orange-700'
                      : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700'
                  }`}
                >
                  <span>View Details</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
